import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Minus, ShoppingBag, MapPin, BookOpen, Sparkles, AlertTriangle, ChevronLeft, Heart, GitCompareArrows, Star, QrCode, X, Share2, Copy, MessageCircle } from "lucide-react";
import { api, fmtPrice, FLAVOR_LABELS, apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useCompare } from "@/context/CompareContext";
import { useReveal } from "@/hooks/useReveal";
import LandFingerprint from "@/components/LandFingerprint";
import ProductCard from "@/components/ProductCard";

function Reveal({ children, className = "" }) {
  const [ref, inView] = useReveal();
  return <div ref={ref} className={`${className} ${inView ? "animate-fade-up" : "opacity-0"}`}>{children}</div>;
}

function Station({ num, title, children, icon: Icon }) {
  return (
    <Reveal className="border-t border-olive/10 py-14 sm:py-16">
      <div className="mb-7 flex items-center gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-gold/40 bg-gold/10 font-display text-sm font-bold text-gold">{num}</span>
        {Icon && <Icon className="h-5 w-5 text-olive" />}
        <h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">{title}</h2>
      </div>
      {children}
    </Reveal>
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const { add } = useCart();
  const { user, isFavorite, toggleFavorite } = useAuth();
  const { has, toggle } = useCompare();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [activeIng, setActiveIng] = useState(null);
  const [loading, setLoading] = useState(true);
  const [qr, setQr] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const openQr = async () => {
    setQrOpen(true);
    if (!qr) {
      try {
        const url = `${window.location.origin}/p/${product.sku}`;
        const r = await api.get("/qr", { params: { data: url } });
        setQr(r.data.image);
      } catch { /* ignore */ }
    }
  };

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`).then((r) => { setProduct(r.data); setLoading(false); setQty(1); })
      .catch(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) return <div className="flex min-h-screen items-center justify-center pt-24 text-ink/50">جارِ التحميل...</div>;
  if (!product) return <div className="flex min-h-screen flex-col items-center justify-center gap-4 pt-24"><p className="text-ink/60">لم يُعثر على المنتج.</p><Link to="/store" className="text-olive">العودة للمتجر</Link></div>;

  const flavorKeys = Object.keys(product.flavor || {}).filter((k) => product.flavor[k] > 0);
  const journeyStages = [
    ["land", "الأرض"], ["farm", "المزرعة"], ["production", "التصنيع"],
    ["packaging", "التغليف"], ["shipping", "الشحن"], ["table", "مائدتك"],
  ].filter(([k]) => k === "table" || product.journey?.[k]);
  const nutrition = product.nutrition || {};
  const hasNutrition = Object.keys(nutrition).length > 0;

  return (
    <div className="pb-28 lg:pb-0">
      {/* breadcrumb + hero image */}
      <div className="mx-auto max-w-7xl px-6 pt-24">
        <div className="mb-4 flex items-center gap-1.5 text-sm text-ink/50">
          <Link to="/store" className="hover:text-olive">المتجر</Link>
          <ChevronLeft className="h-3.5 w-3.5" />
          <Link to={`/store?geo=${product.geo_source}`} className="flex items-center gap-1 hover:text-olive"><MapPin className="h-3.5 w-3.5" /> {product.geo_source_ar}</Link>
          <ChevronLeft className="h-3.5 w-3.5" />
          <span className="text-ink/70">{product.name_ar}</span>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          {/* 1. Big image */}
          <div className="relative overflow-hidden rounded-[2rem] bg-sand">
            <img src={product.images?.main} alt={product.name_ar} className="aspect-square w-full object-cover" />
            {product.is_demo && <span className="absolute right-4 top-4 rounded-md bg-black/55 px-2 py-1 text-xs text-white/90">تجريبي</span>}
          </div>

          {/* buy panel */}
          <div className="flex flex-col justify-center">
            <div className="mb-3 flex flex-wrap gap-2">
              {(product.tags || []).includes("bestseller") && <span className="rounded-full bg-palred px-3 py-1 text-xs font-semibold text-white">الأكثر طلباً</span>}
              {product.organic && <span className="rounded-full bg-olive px-3 py-1 text-xs font-semibold text-white">عضوي موثّق</span>}
              {product.natural && <span className="rounded-full bg-olive/10 px-3 py-1 text-xs font-semibold text-olive">طبيعي</span>}
            </div>
            <h1 data-testid="product-title" className="font-display text-3xl font-extrabold text-ink sm:text-4xl">{product.name_ar}</h1>
            <p className="mt-3 text-lg text-ink/60">{product.short_story}</p>
            <div className="mt-6 flex items-baseline gap-3">
              <span className="font-display text-3xl font-bold text-olive-dark">{fmtPrice(product.price)}</span>
              <span className="text-ink/50">/ {product.weight_grams} غم</span>
            </div>

            {product.allergens?.length > 0 && (
              <div className="mt-5 flex items-center gap-2 rounded-xl bg-palred/8 px-4 py-3 text-sm text-palred">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>يحتوي على مسببات حساسية: {product.allergens.join("، ")}</span>
              </div>
            )}

            <div className="mt-7 flex items-center gap-3">
              <div className="flex items-center rounded-full border border-olive/20">
                <button data-testid="qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="flex h-12 w-12 items-center justify-center text-olive"><Minus className="h-4 w-4" /></button>
                <span data-testid="qty-value" className="w-8 text-center font-semibold">{qty}</span>
                <button data-testid="qty-increase" onClick={() => setQty((q) => q + 1)} className="flex h-12 w-12 items-center justify-center text-olive"><Plus className="h-4 w-4" /></button>
              </div>
              <button data-testid="product-add-to-cart" onClick={() => add(product, qty)}
                className="btn-shine flex flex-1 items-center justify-center gap-2 rounded-full bg-olive px-6 py-3.5 font-semibold text-ivory transition-[background-color,transform] hover:bg-olive-dark active:scale-[0.98]">
                <ShoppingBag className="h-5 w-5" /> أضف إلى سلتك
              </button>
            </div>

            <div className="mt-3 flex flex-wrap gap-3">
              <button data-testid="product-favorite" onClick={() => user ? toggleFavorite(product.sku) : toast.error("سجّل الدخول لحفظ المفضلة")}
                className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${user && isFavorite(product.sku) ? "border-palred bg-palred/5 text-palred" : "border-olive/20 text-ink/70 hover:border-palred/40"}`}>
                <Heart className={`h-4 w-4 ${user && isFavorite(product.sku) ? "fill-current" : ""}`} /> المفضلة
              </button>
              <button data-testid="product-compare" onClick={() => toggle(product.sku, product.name_ar)}
                className={`flex items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-colors ${has(product.sku) ? "border-olive bg-olive/5 text-olive" : "border-olive/20 text-ink/70 hover:border-olive/40"}`}>
                <GitCompareArrows className="h-4 w-4" /> قارن
              </button>
              <button data-testid="product-qr" onClick={openQr}
                className="flex items-center gap-2 rounded-full border border-olive/20 px-4 py-2.5 text-sm font-medium text-ink/70 transition-colors hover:border-olive/40">
                <QrCode className="h-4 w-4" /> رمز QR
              </button>
              <div className="relative">
                <button data-testid="share-product" onClick={() => setShareOpen((v) => !v)}
                  className="flex items-center gap-2 rounded-full border border-olive/20 px-4 py-2.5 text-sm font-medium text-ink/70 transition-colors hover:border-olive/40">
                  <Share2 className="h-4 w-4" /> مشاركة
                </button>
                {shareOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShareOpen(false)} />
                    <div data-testid="share-menu" className="absolute bottom-full right-0 z-50 mb-2 w-56 overflow-hidden rounded-2xl border border-olive/15 bg-white shadow-xl">
                      <button data-testid="share-whatsapp"
                        onClick={() => {
                          const url = `${window.location.origin}/api/og/product/${product.slug}`;
                          window.open(`https://wa.me/?text=${encodeURIComponent(`${product.name_ar} | سهول جنين\n${url}`)}`, "_blank");
                          setShareOpen(false);
                        }}
                        className="flex w-full items-center gap-2.5 px-4 py-3 text-right text-sm font-medium text-ink/80 transition-colors hover:bg-olive/5">
                        <MessageCircle className="h-4 w-4 text-olive" /> أرسل عبر واتساب
                      </button>
                      <button data-testid="share-copy"
                        onClick={async () => {
                          const url = `${window.location.origin}/api/og/product/${product.slug}`;
                          const shareBody = `${product.name_ar} | سهول جنين\n${url}`;
                          try {
                            await navigator.clipboard.writeText(shareBody);
                            toast.success("تم نسخ رابط المشاركة — يظهر كبطاقة معاينة في واتساب");
                          } catch {
                            try {
                              const ta = document.createElement("textarea");
                              ta.value = shareBody; ta.style.position = "fixed"; ta.style.opacity = "0";
                              document.body.appendChild(ta); ta.select();
                              document.execCommand("copy"); ta.remove();
                              toast.success("تم نسخ رابط المشاركة — يظهر كبطاقة معاينة في واتساب");
                            } catch { toast.error("تعذّر نسخ الرابط"); }
                          }
                          setShareOpen(false);
                        }}
                        className="flex w-full items-center gap-2.5 border-t border-olive/10 px-4 py-3 text-right text-sm font-medium text-ink/80 transition-colors hover:bg-olive/5">
                        <Copy className="h-4 w-4 text-olive" /> نسخ الرابط
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="mt-7"><LandFingerprint fingerprint={product.fingerprint} /></div>
          </div>
        </div>
      </div>

      {/* Stations */}
      <div className="mx-auto max-w-4xl px-6">
        {product.origin_text && (
          <Station num="2" title="من أين جاء؟" icon={MapPin}>
            <div className="grid items-center gap-8 md:grid-cols-2">
              <p className="font-display text-xl leading-loose text-ink/75 sm:text-2xl sm:leading-loose">
                <span className="text-gold">«</span>{product.origin_text}<span className="text-gold">»</span>
              </p>
              {product.images?.origin && (
                <img src={product.images.origin} alt="المنشأ" loading="lazy" decoding="async"
                  className="aspect-[4/3] w-full rounded-[2rem] object-cover shadow-[0_24px_60px_-24px_rgba(44,62,45,0.4)]" />
              )}
            </div>
          </Station>
        )}

        {/* 3 + know what you eat */}
        {product.ingredients?.length > 0 && (
          <Station num="3" title="مِمَّ يتكوّن؟ — اعرف ما تأكل" icon={Sparkles}>
            <p className="mb-5 text-ink/55">اضغط على أي مكوّن لتعرف مصدره وتفاصيله.</p>
            <div className="flex flex-wrap gap-2.5">
              {product.ingredients.map((ing, i) => (
                <button key={i} data-testid={`ingredient-${i}`} onClick={() => setActiveIng(activeIng === i ? null : i)}
                  className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                    activeIng === i ? "border-olive bg-olive text-ivory" : "border-olive/20 bg-white text-ink/75 hover:border-olive/50"}`}>
                  {ing.name_ar}
                </button>
              ))}
            </div>
            {activeIng != null && product.ingredients[activeIng] && (
              <div className="mt-5 rounded-2xl border border-olive/15 bg-olive/[0.04] p-5">
                <div className="font-display font-semibold text-olive-dark">{product.ingredients[activeIng].name_ar}</div>
                {product.ingredients[activeIng].source && <div className="mt-1 text-sm text-ink/70">المصدر: {product.ingredients[activeIng].source}</div>}
                {product.ingredients[activeIng].note && <div className="mt-1 text-sm text-ink/60">{product.ingredients[activeIng].note}</div>}
              </div>
            )}
          </Station>
        )}

        {product.usage?.length > 0 && (
          <Station num="4" title="كيف يُستخدم؟">
            <div className="grid gap-3 sm:grid-cols-2">
              {product.usage.map((u, i) => (
                <div key={i} className="rounded-2xl border border-olive/10 bg-white p-4">
                  <div className="mb-1 font-semibold text-olive">{u.context}</div>
                  <div className="text-sm text-ink/65">{u.text}</div>
                </div>
              ))}
            </div>
          </Station>
        )}

        {/* Journey timeline */}
        {journeyStages.length > 1 && (
          <Station num="5" title="رحلة المنتج">
            <JourneyTimeline stages={journeyStages} journey={product.journey} />
          </Station>
        )}

        {/* Flavor map */}
        {flavorKeys.length > 0 && (
          <Station num="6" title="خريطة النكهات">
            <div className="space-y-3">
              {flavorKeys.map((k) => (
                <div key={k} className="flex items-center gap-3">
                  <span className="w-16 text-sm text-ink/70">{FLAVOR_LABELS[k]}</span>
                  <div className="h-3 flex-1 overflow-hidden rounded-full bg-olive/10">
                    <div className="h-full rounded-full bg-olive transition-all duration-700" style={{ width: `${(product.flavor[k] / 5) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </Station>
        )}

        {/* Info card / nutrition */}
        <Station num="7" title="بطاقة المعلومات">
          <div className="grid gap-4 sm:grid-cols-2">
            <InfoRow label="المصدر الجغرافي" value={product.geo_source_ar} />
            <InfoRow label="رقم المنتج" value={product.sku} />
            <InfoRow label="الوزن" value={`${product.weight_grams} غم`} />
            <InfoRow label="مسببات الحساسية" value={product.allergens?.length ? product.allergens.join("، ") : "لا يوجد مسجّل"} highlight={product.allergens?.length > 0} />
          </div>
          {hasNutrition && (
            <div className="mt-4 rounded-2xl border border-olive/10 bg-white p-5">
              <div className="mb-3 font-semibold text-ink">القيمة الغذائية {nutrition.serving && `(لكل ${nutrition.serving})`}</div>
              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                {nutrition.calories != null && <NutriBox label="سعرات" value={nutrition.calories} />}
                {nutrition.fat_g != null && <NutriBox label="دهون" value={`${nutrition.fat_g}غ`} />}
                {nutrition.carbs_g != null && <NutriBox label="كربوهيدرات" value={`${nutrition.carbs_g}غ`} />}
                {nutrition.protein_g != null && <NutriBox label="بروتين" value={`${nutrition.protein_g}غ`} />}
              </div>
            </div>
          )}
        </Station>

        {/* Heritage — only if approved */}
        {product.heritage?.approved && (
          <Station num="8" title="صلة المنتج بالتراث الإسلامي" icon={BookOpen}>
            <div className="rounded-2xl border border-gold/30 bg-gold/[0.06] p-6">
              {product.heritage.quote && <p className="mb-3 font-display text-lg leading-loose text-olive-dark">{product.heritage.quote}</p>}
              <p className="text-ink/70">{product.heritage.text}</p>
              <div className="mt-4 space-y-1 text-sm text-ink/55">
                {product.heritage.source && <div>المصدر: {product.heritage.source}</div>}
                {product.heritage.reference && <div>المرجع: {product.heritage.reference}</div>}
                {product.heritage.hadith_grade && <div>درجة الحديث: {product.heritage.hadith_grade}</div>}
                {product.heritage.note && <div className="text-ink/50">{product.heritage.note}</div>}
              </div>
              {product.heritage.link && (
                <a href={product.heritage.link} target="_blank" rel="noreferrer" className="mt-3 inline-block font-medium text-olive link-underline">اقرأ المزيد</a>
              )}
            </div>
          </Station>
        )}
      </div>

      {/* Reviews */}
      <div className="mx-auto max-w-4xl px-6">
        <Reviews sku={product.sku} />
      </div>

      {/* Related */}
      {product.related?.length > 0 && (
        <div className="mx-auto max-w-7xl px-6 pt-16">
          <h2 className="mb-8 font-display text-2xl font-bold text-ink">منتجات قد تناسبه</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-6">
            {product.related.map((p, i) => <ProductCard key={p.sku} product={p} index={i} />)}
          </div>
        </div>
      )}

      {/* QR modal */}
      {qrOpen && (
        <div data-testid="qr-modal" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-6" onClick={() => setQrOpen(false)}>
          <div className="w-full max-w-xs rounded-3xl bg-ivory p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display font-bold text-ink">رمز المنتج</h3>
              <button onClick={() => setQrOpen(false)}><X className="h-5 w-5 text-ink/50" /></button>
            </div>
            {qr ? <img src={qr} alt="QR" className="mx-auto h-56 w-56 rounded-xl" /> : <div className="py-20 text-ink/40">جارِ التوليد...</div>}
            <p className="mt-3 text-sm text-ink/60">{product.name_ar}</p>
            <p className="text-xs text-ink/40">امسح الرمز لعرض صفحة سريعة للمنتج ({product.sku}).</p>
          </div>
        </div>
      )}

      {/* sticky mobile add to cart */}
      <div className="fixed inset-x-0 bottom-0 z-40 flex items-center gap-3 border-t border-olive/10 bg-ivory/95 p-3 backdrop-blur lg:hidden">
        <div className="font-display text-lg font-bold text-olive-dark">{fmtPrice(product.price * qty)}</div>
        <button data-testid="sticky-add-to-cart" onClick={() => add(product, qty)}
          className="flex flex-1 items-center justify-center gap-2 rounded-full bg-olive py-3 font-semibold text-ivory">
          <ShoppingBag className="h-5 w-5" /> أضف إلى سلتك
        </button>
      </div>
    </div>
  );
}

function JourneyTimeline({ stages, journey }) {
  const [ref, inView] = useReveal();
  return (
    <div ref={ref} className="relative pr-6">
      <div className="absolute right-[7px] top-2 w-0.5 bg-olive/15" style={{ height: "calc(100% - 1rem)" }} />
      <div className="timeline-line absolute right-[7px] top-2 w-0.5 bg-olive" style={{ height: inView ? "calc(100% - 1rem)" : "0%" }} />
      <div className="space-y-6">
        {stages.map(([key, label], i) => (
          <div key={key} className="relative flex gap-4" style={{ transitionDelay: `${i * 120}ms` }}>
            <span className={`absolute right-0 top-1.5 h-4 w-4 -translate-x-0 rounded-full border-2 transition-colors duration-500 ${inView ? "border-olive bg-olive" : "border-olive/30 bg-ivory"}`} style={{ transitionDelay: `${i * 150}ms` }} />
            <div className="pr-8">
              <div className="font-display font-semibold text-ink">{label}</div>
              {journey?.[key] && <div className="text-sm text-ink/60">{journey[key]}</div>}
              {key === "table" && <div className="text-sm text-earth">وصلت إليك 🌿</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const InfoRow = ({ label, value, highlight }) => (  <div className="flex items-center justify-between rounded-xl border border-olive/10 bg-white px-4 py-3">
    <span className="text-sm text-ink/50">{label}</span>
    <span className={`text-sm font-medium ${highlight ? "text-palred" : "text-ink"}`}>{value}</span>
  </div>
);

const NutriBox = ({ label, value }) => (
  <div className="rounded-xl bg-olive/5 p-3 text-center">
    <div className="font-display text-lg font-bold text-olive-dark">{value}</div>
    <div className="text-xs text-ink/50">{label}</div>
  </div>
);

function Reviews({ sku }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ rating: 5, liked: true, matches_desc: true, packaging_good: true, buy_again: true, comment: "" });
  const load = () => api.get(`/products/${sku}/reviews`).then((r) => setReviews(r.data));
  useEffect(() => { load(); }, [sku]);

  const submit = async () => {
    try {
      await api.post(`/products/${sku}/reviews`, form);
      toast.success("تم إرسال تقييمك للمراجعة قبل النشر");
      setShowForm(false);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const Q = ({ k, label }) => (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="accent-olive" /> {label}
    </label>
  );

  return (
    <section className="border-t border-olive/10 py-12">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold text-ink">التقييمات ({reviews.length})</h2>
        {user && <button data-testid="write-review" onClick={() => setShowForm((v) => !v)} className="rounded-full bg-olive px-5 py-2.5 text-sm font-medium text-ivory">اكتب تقييماً</button>}
      </div>

      {showForm && (
        <div className="mb-6 rounded-2xl border border-olive/10 bg-white p-5">
          <div className="mb-3 flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`star-${n}`} onClick={() => setForm({ ...form, rating: n })}>
                <Star className={`h-6 w-6 ${n <= form.rating ? "fill-gold text-gold" : "text-ink/20"}`} />
              </button>
            ))}
          </div>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <Q k="liked" label="هل أعجبك؟" />
            <Q k="matches_desc" label="مطابق للوصف؟" />
            <Q k="packaging_good" label="التغليف جيد؟" />
            <Q k="buy_again" label="ستشتريه مجدداً؟" />
          </div>
          <textarea data-testid="review-comment" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={3} placeholder="اكتب رأيك..." className="w-full rounded-xl border border-olive/20 px-4 py-3 outline-none" />
          <button data-testid="submit-review" onClick={submit} className="mt-3 rounded-full bg-olive px-6 py-2.5 font-semibold text-ivory">إرسال التقييم</button>
          <p className="mt-2 text-xs text-ink/40">يُراجع تقييمك قبل النشر. التقييم متاح لمن اشترى المنتج فقط.</p>
        </div>
      )}

      {reviews.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-olive/20 py-10 text-center text-ink/50">لا توجد تقييمات معتمدة بعد.</p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="rounded-2xl border border-olive/10 bg-white p-4">
              <div className="flex items-center justify-between">
                <span className="font-medium text-ink">{r.user_name}</span>
                <span className="flex items-center gap-0.5">{Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-gold text-gold" />)}</span>
              </div>
              {r.comment && <p className="mt-2 text-sm text-ink/70">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

